import type {
  NetworkDashboardAvailableStore,
  NetworkDashboardStoreAgeFilter,
} from '@gozoki/api-types'
import { DateTime } from 'luxon'
import { storeAgeFromFilter } from '~/components/dashboards/filters/store-age'

// Un id boutique garanti inexistant (≤ max du schéma vine `hrAnalyticsBaseSchema`) : renvoyé quand le
// filtre ancienneté est actif mais qu'aucune boutique du périmètre n'entre dans la tranche → le backend
// renvoie 0 shift (KPI à zéro) au lieu de retomber sur tout le réseau (un `storeIds` vide est interprété
// par `resolveStoreIdsFilter` comme « pas de filtre »).
export const AGE_FILTER_NO_MATCH_SENTINEL = 2_147_483_647

/** A store-age selection cannot be resolved safely before the store metadata is available. */
export function selectionNeedsAvailableStores(
  storeAgeFilter: NetworkDashboardStoreAgeFilter,
): boolean {
  return storeAgeFilter !== 'default'
}

// Périmètre par défaut du dashboard RH = boutiques « matures » (ouvertes depuis > 3 mois à la fin de la
// période). Référence = date d'OUVERTURE (`openingDate`), pas 1re vente — même seuil que le backend Trends
// (`getHrDashboardTrends.ts`, `MIN_STORE_AGE_MONTHS = 3`), pour que TOUS les widgets analytiques partent du
// même périmètre à l'ouverture sans filtre.
const MATURE_STORE_MIN_MONTHS = 3

export function matureStoreIdsByOpeningDate(
  availableStores: NetworkDashboardAvailableStore[],
  periodEndIso: string,
): number[] {
  const matureThresholdIso = DateTime.fromISO(periodEndIso)
    .minus({ months: MATURE_STORE_MIN_MONTHS })
    .toISODate()!
  return availableStores
    .filter((s) => {
      // `openingDate` arrive en timestamp UTC séparé par un espace (`2026-08-31 22:00:00+00`, pas
      // `YYYY-MM-DD`) → on remet le `T` ISO puis on ramène au jour calendaire Paris (même convention que
      // `ageReferenceDate` et l'affichage app) avant de comparer.
      const openingIso = DateTime.fromISO(s.openingDate.replace(' ', 'T'), { setZone: true })
        .setZone('Europe/Paris')
        .toISODate()
      return openingIso !== null && openingIso <= matureThresholdIso
    })
    .map((s) => s.id)
}

// Périmètre boutique effectif = sélection géographique ∩ tranche d'ancienneté. La tranche est résolue
// côté client depuis `availableStores.ageReferenceDate` (même référence — 1re vente / repli ouverture —
// que le SQL backend), donc pas besoin de câbler le filtre jusqu'aux endpoints : on rabat tout sur
// `storeIds`, déjà géré par les endpoints du dashboard RH. Partagé par le dashboard et ses modales de
// détail pour qu'ils appliquent EXACTEMENT le même périmètre.
export function resolveEffectiveStoreIds(
  storeIds: number[] | undefined,
  storeAgeFilter: NetworkDashboardStoreAgeFilter,
  ageStoreIds: number[] | undefined,
  availableStores: NetworkDashboardAvailableStore[],
  periodEndIso: string,
  opts?: { applyMatureDefault?: boolean },
): number[] | undefined {
  const applyMatureDefault = opts?.applyMatureDefault ?? true

  // Ouverture sans filtre (`default`) : on applique le périmètre par défaut « boutiques > 3 mois » (sauf
  // opt-out, ex. panneau Plannings S+1 qui reste sur toutes les boutiques). Une sélection géographique
  // explicite reste un filtre → on la respecte telle quelle, sans imposer la maturité.
  if (storeAgeFilter === 'default') {
    if (storeIds && storeIds.length > 0) return storeIds
    if (!applyMatureDefault || availableStores.length === 0) return storeIds
    const mature = matureStoreIdsByOpeningDate(availableStores, periodEndIso)
    return mature.length > 0 ? mature : [AGE_FILTER_NO_MATCH_SENTINEL]
  }

  // Boutiques pas encore chargées (rien pour résoudre la tranche) : on garde la sélection géographique
  // telle quelle plutôt que de flasher un dashboard à zéro le temps du fetch.
  if (availableStores.length === 0) return storeIds

  const ageState = storeAgeFromFilter(storeAgeFilter, ageStoreIds, availableStores, periodEndIso)
  const ageIds = ageState.mode === 'custom' ? ageState.selectedStoreIds : new Set<number>()

  const effective =
    storeIds && storeIds.length > 0 ? storeIds.filter((id) => ageIds.has(id)) : [...ageIds]

  return effective.length > 0 ? effective : [AGE_FILTER_NO_MATCH_SENTINEL]
}
